/**
 * Pinned projects, and the row model both project pickers draw (LED-1151).
 *
 * A workspace can carry 100+ projects. Both pickers — the nav header's and
 * Home's "Find a project" — therefore open on a PINNED group and then every
 * other project, most recently walked first, and narrow by typing.
 *
 * Pins are a per-viewer display preference, so they live in `localStorage`
 * and are allowed to be missing: a private window throws from the accessor
 * itself, and the pickers then simply open with no pinned group.
 */

import { useEffect, useState } from "react";

export const PINS_KEY = "ledai.pinnedProjects";
const PINS_EVENT = "ledai:pins";

export function readPins(): string[] {
  try {
    const raw = localStorage.getItem(PINS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

export function writePins(ids: string[]): void {
  try {
    localStorage.setItem(PINS_KEY, JSON.stringify(ids));
  } catch {
    // Storage unavailable — the pin holds for this page only.
  }
  if (typeof window !== "undefined") window.dispatchEvent(new Event(PINS_EVENT));
}

/** The pinned ids, live across both pickers and across tabs. */
export function usePins(): [string[], (id: string) => void] {
  const [pins, setPins] = useState<string[]>(() => readPins());
  useEffect(() => {
    const sync = () => setPins(readPins());
    window.addEventListener(PINS_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(PINS_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  const toggle = (id: string) => {
    const current = readPins();
    const next = current.includes(id) ? current.filter((pin) => pin !== id) : [...current, id];
    setPins(next);
    writePins(next);
  };
  return [pins, toggle];
}

/** What a picker knows about one project. */
export interface PickerProject {
  id: string;
  name: string;
  syncSource?: "procore" | "autodesk";
  /** ISO — the newest walk, or absent when never walked. */
  lastWalkAt?: string;
  toReview: number;
}

export type PickerSort = "recent" | "attention" | "az";

export type PickerRow =
  | { type: "head"; label: string }
  | { type: "project"; project: PickerProject; index: number; pinned: boolean };

function byRecent(a: PickerProject, b: PickerProject): number {
  const at = a.lastWalkAt ? Date.parse(a.lastWalkAt) : 0;
  const bt = b.lastWalkAt ? Date.parse(b.lastWalkAt) : 0;
  return bt - at || label(a).localeCompare(label(b));
}

export function label(project: PickerProject): string {
  return project.name || project.id;
}

const SORTS: Record<PickerSort, (a: PickerProject, b: PickerProject) => number> = {
  recent: byRecent,
  attention: (a, b) => b.toReview - a.toReview || byRecent(a, b),
  az: (a, b) => label(a).localeCompare(label(b)),
};

const SORT_HEAD: Record<PickerSort, string> = {
  recent: "ALL · MOST RECENT",
  attention: "NEEDS ATTENTION FIRST",
  az: "ALL · A–Z",
};

/**
 * The rows a picker draws, in order, with each project's keyboard index.
 * A query flattens the list (no groups): the reader is looking for one name.
 */
export function pickerRows(
  projects: PickerProject[],
  pins: string[],
  query: string,
  sort: PickerSort = "recent",
): { rows: PickerRow[]; flat: PickerProject[] } {
  const needle = query.trim().toLowerCase();
  const sorted = projects
    .filter((project) => !needle || label(project).toLowerCase().includes(needle))
    .sort(SORTS[sort]);
  const pinned = sorted.filter((project) => pins.includes(project.id));
  const others = sorted.filter((project) => !pins.includes(project.id));
  const rows: PickerRow[] = [];
  let index = 0;
  if (!needle && pinned.length) {
    rows.push({ type: "head", label: "PINNED" });
    for (const project of pinned) rows.push({ type: "project", project, index: index++, pinned: true });
    if (others.length) rows.push({ type: "head", label: SORT_HEAD[sort] });
    for (const project of others) rows.push({ type: "project", project, index: index++, pinned: false });
    return { rows, flat: [...pinned, ...others] };
  }
  for (const project of sorted) {
    rows.push({ type: "project", project, index: index++, pinned: pins.includes(project.id) });
  }
  return { rows, flat: sorted };
}
